// eslint-disable-next-line import/no-restricted-paths
import isEqual from "lodash.isequal";

import type { InputMaybe, S3Object } from "@/lib/gql/__generated__/graphql";

import { DUMMY_IMAGE } from "@/constants/dummy-image";

import type { ForArrayType, ForAutoGenerator } from "../../_model/types/auto-generator-for-view-model";

type MainKeyVisualType = ForAutoGenerator<S3Object | undefined, "">;
type KeyVisualsType =
	| ForArrayType<S3Object[]>
	| ForArrayType<S3Object[] | undefined>
	| ForArrayType<InputMaybe<S3Object[]> | undefined>
	| undefined;

type Props = {
	mainKeyVisual: MainKeyVisualType;
	keyVisuals: KeyVisualsType;

	onSetMainKeyVisual: <K extends "mainKeyVisual">(
		values: { key: K; value: ForAutoGenerator<S3Object, ""> }[],
	) => void;
	onSetKeyVisuals: <K extends "keyVisuals">(values: { key: K; value: ForArrayType<S3Object[]> }[]) => void;
};

export type UseMainKeyVisualAndKeyVisualsReturnType = {
	mainKeyVisual: MainKeyVisualType;
	isEditedMainKV: boolean;
	hasErrorMainKV: boolean;
	errorMessageMainKV: string | undefined;
	onSetMainKeyVisual: (value: S3Object | null) => void;
	keyVisuals: KeyVisualsType;
	onSetKeyVisuals: (value: S3Object[]) => void;
};

export const useMainKeyVisualAndKeyVisuals = ({
	mainKeyVisual,
	keyVisuals,
	onSetMainKeyVisual: _onSetMainKeyVisual,
	onSetKeyVisuals: _onSetKeyVisuals,
}: Props) => {
	const isEditedMainKV = !isEqual(mainKeyVisual, DUMMY_IMAGE.s3Object);
	const hasErrorMainKV =
		Boolean(mainKeyVisual?.bucket?.error) ||
		Boolean(mainKeyVisual?.key?.error) ||
		Boolean(mainKeyVisual?.mimeType?.error) ||
		Boolean(mainKeyVisual?.fileName?.error) ||
		Boolean(mainKeyVisual?.region?.error) ||
		false;
	const errorMessageMainKV =
		mainKeyVisual?.bucket?.error ||
		mainKeyVisual?.key?.error ||
		mainKeyVisual?.mimeType?.error ||
		mainKeyVisual?.fileName?.error ||
		mainKeyVisual?.region?.error;

	const onSetMainKeyVisual = (value: S3Object | null) => {
		const s3Object = value;

		const newMainKeyVisual = {
			key: {
				isEdited: true,
				value: s3Object?.key ?? "",
			},
			bucket: {
				isEdited: true,
				value: s3Object?.bucket ?? "",
			},
			mimeType: {
				isEdited: true,
				value: s3Object?.mimeType ?? "",
			},
			fileName: {
				isEdited: true,
				value: s3Object?.fileName ?? "",
			},
			region: {
				isEdited: true,
				value: s3Object?.region ?? "",
			},
		} satisfies MainKeyVisualType;

		_onSetMainKeyVisual([
			{
				key: "mainKeyVisual",
				value: newMainKeyVisual,
			},
		]);
	};

	const onSetKeyVisuals = (value: S3Object[]) => {
		const newKeyVisualsValue = value.map((v) => {
			return {
				key: {
					isEdited: true,
					value: v.key,
				},
				bucket: {
					isEdited: true,
					value: v.bucket,
				},
				mimeType: {
					isEdited: true,
					value: v.mimeType,
				},
				fileName: {
					isEdited: true,
					value: v.fileName,
				},
				region: {
					isEdited: true,
					value: v.region,
				},
			} satisfies MainKeyVisualType;
		});

		const newMainKeyVisuals = {
			isEdited: true,
			hasError: false,
			value: newKeyVisualsValue,
		} satisfies KeyVisualsType;

		_onSetKeyVisuals([{ key: "keyVisuals", value: newMainKeyVisuals }]);
	};

	return {
		mainKeyVisual,
		isEditedMainKV,
		hasErrorMainKV,
		errorMessageMainKV,
		onSetMainKeyVisual,

		keyVisuals,
		onSetKeyVisuals,
	};
};
